import React, { useState, useEffect, useCallback } from 'react';
import { SentenceDisplay } from './SentenceDisplay';
import { Play, Pause, Square } from 'lucide-react';
import CountdownGauge from './CountdownGauge';
import SimpleDialog from './SimpleDialog';
import '../styles/MainContent.css';

export function MainContent({
  module,
  subModule,
  seconds,
  sentenceCount,
  sidebarOpen,
  closeSidebar,
  onExerciseStateChange,
  fetchSentences,
  sentences,
  error,
  resetError,
  markSentenceAsDisplayed, // Add this prop
}) {
  const [exerciseState, setExerciseState] = useState('idle');
  const [currentSentence, setCurrentSentence] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(seconds);
  const [phase, setPhase] = useState('show');
  const [sentenceComplete, setSentenceComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => {
    if (error) {
      setWarningMessage(error);
      setShowWarning(true);
      setExerciseState('idle');
      setIsTransitioning(false);
      onExerciseStateChange('idle');
    }
  }, [error, onExerciseStateChange]);

  useEffect(() => {
    let timer;
    if (exerciseState === 'running' && sentences.length > 0 && showContent) {
      timer = setInterval(() => {
        switch (phase) {
          case 'show':
            if (sentenceComplete) {
              setPhase('countdown');
              setRemainingSeconds(seconds);
              markSentenceAsDisplayed(
                sentences[currentSentence],
                module,
                subModule
              );
            }
            break;
          case 'countdown':
            if (remainingSeconds > 0) {
              setRemainingSeconds((prev) => prev - 1);
            } else {
              setPhase('pause1');
            }
            break;
          case 'pause1':
            setPhase('hide');
            break;
          case 'hide':
            setPhase('pause2');
            break;
          case 'pause2':
            if (currentSentence + 1 < sentences.length) {
              setCurrentSentence((prev) => prev + 1);
              setSentenceComplete(false);
              setPhase('show');
            } else {
              setExerciseState('finished');
              onExerciseStateChange('finished');
            }
            break;
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [
    exerciseState,
    sentences,
    seconds,
    remainingSeconds,
    phase,
    sentenceComplete,
    currentSentence,
    onExerciseStateChange,
    showContent,
    markSentenceAsDisplayed,
    module,
    subModule,
  ]);

  const handleStart = async () => {
    if (!module || !subModule) {
      setWarningMessage('モジュールとクラスを選択してください。');
      setShowWarning(true);
      return;
    }

    closeSidebar();
    setIsTransitioning(true);
    setShowContent(false);
    setExerciseState('loading');
    setShowWarning(false);
    setWarningMessage('');

    setIsLoading(true);
    resetError();
    try {
      await fetchSentences();
      setIsLoading(false);
      setTimeout(() => {
        setShowContent(true);
        setExerciseState('running');
        setPhase('show');
        setSentenceComplete(false);
        setCurrentSentence(0);
        setRemainingSeconds(seconds);
        setIsTransitioning(false);
        onExerciseStateChange('running');
      }, 1000);
    } catch (error) {
      setIsLoading(false);
      setWarningMessage(
        error.message ||
          '予期しないエラーが発生しました。もう一度お試しください。'
      );
      setShowWarning(true);
      setExerciseState('idle');
      setIsTransitioning(false);
      onExerciseStateChange('idle');
    }
  };

  const handleCloseWarning = () => {
    setShowWarning(false);
    setWarningMessage('');
    setExerciseState('idle');
    setIsTransitioning(false);
    onExerciseStateChange('idle');
    resetError();
  };

  const handlePause = () => {
    setExerciseState('paused');
    onExerciseStateChange('paused');
  };

  const handleResume = () => {
    setExerciseState('running');
    onExerciseStateChange('running');
  };

  const handleExit = () => {
    setExerciseState('idle');
    setCurrentSentence(0);
    setPhase('show');
    setSentenceComplete(false);
    setRemainingSeconds(seconds);
    setShowContent(false);
    setIsTransitioning(false);
    onExerciseStateChange('idle');
  };

  const handleNext = () => {
    if (currentSentence + 1 < sentences.length) {
      setCurrentSentence((prev) => prev + 1);
      setSentenceComplete(false);
      setPhase('show');
      setRemainingSeconds(seconds);
    } else {
      handleExit();
    }
  };

  const handleSentenceComplete = useCallback(() => {
    setSentenceComplete(true);
  }, []);

  return (
    <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <SimpleDialog
        isOpen={isLoading}
        message="読み込んでいます。しばらくお待ちください。"
      />
      <SimpleDialog
        isOpen={showWarning}
        message={warningMessage}
        onClose={handleCloseWarning}
        showCloseButton={true}
      />
      <div className="content-wrapper">
        <div className="sentence-counter">
          {currentSentence + 1} / {sentences.length}
        </div>
        {exerciseState === 'idle' && !isTransitioning ? (
          <button className="start-button" onClick={handleStart}>
            <Play size={24} />
            始める
          </button>
        ) : exerciseState === 'finished' && !isTransitioning ? (
          <div className="end-message">
            <h2>お疲れ様！</h2>
            <button className="start-button" onClick={handleStart}>
              <Play size={24} />
              もう一度始める
            </button>
          </div>
        ) : (
          showContent && (
            <>
              <SentenceDisplay
                sentence={sentences[currentSentence]}
                visible={phase !== 'hide' && phase !== 'pause2'}
                onComplete={handleSentenceComplete}
              />
              <CountdownGauge
                totalSeconds={seconds}
                remainingSeconds={remainingSeconds}
                phase={phase}
              />
            </>
          )
        )}
      </div>
      {(exerciseState === 'running' || exerciseState === 'paused') &&
        !isTransitioning && (
          <div className="fixed-control-buttons">
            {exerciseState === 'running' ? (
              <button className="pause-button" onClick={handlePause}>
                <Pause size={24} />
                停止
              </button>
            ) : (
              <button className="resume-button" onClick={handleResume}>
                <Play size={24} />
                再開
              </button>
            )}
            <button className="control-button next-button" onClick={handleNext}>
              <Play size={24} />
              次へ
            </button>
            <button className="exit-button" onClick={handleExit}>
              <Square size={24} />
              終了
            </button>
          </div>
        )}
    </div>
  );
}
